import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Clear, Search, AddOutlined } from "@mui/icons-material";
import {
    format,
    startOfMonth,
    endOfMonth,
    subDays,
    subMonths,
    subYears,
    addMonths,
    parseISO,
} from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";


export type FireClientOption = {
    id: string;
    label: string;
};

interface FireQuoteToolbarProps {
    textSearch?: string;
    onChangeTextSearch?: (value: string) => void;
    onClearTextSearch?: () => void;

    showInputSearch?: boolean;

    showDateRange?: boolean;
    onChangeDateStart?: (value: string) => void;
    onChangeDateEnd?: (value: string) => void;

    showStatusSelect?: boolean;
    onChangeStatus?: (value: string) => void;

    showSearchButton?: boolean;
    onClickSearch?: () => void;

    showRefreshButton?: boolean;
    onClickRefresh?: () => void;

    showNewQuoteButton?: boolean;
    onClickNewQuote?: () => void;

    showNewFireButton?: boolean;
    onClickNewFire?: () => void;

    showClientSelect?: boolean;
    onChangeClient?: (clientId: string | null) => void;
}

export function FireQuoteToolbar({
    textSearch = "",
    onChangeTextSearch,
    onClearTextSearch,
    showInputSearch = true,

    showDateRange = true,
    onChangeDateStart,
    onChangeDateEnd,

    showStatusSelect = false,
    onChangeStatus,

    showSearchButton,
    onClickSearch,
    showRefreshButton = true,
    onClickRefresh,

    showNewQuoteButton,
    onClickNewQuote,
    showNewFireButton = true,
    onClickNewFire,

    showClientSelect = false,
    onChangeClient,
}: FireQuoteToolbarProps) {
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const { debounce } = useDebounce();

    // ------------------------
    // Estados de período / datas
    // ------------------------
    const today = useMemo(() => new Date(), []);
    const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
    const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
    const [periodType, setPeriodType] = useState("1_mes");
    const [status, setStatus] = useState("todos");

    // ------------------------
    // AUTOCOMPLETE (cliente) – igual Proposal
    // ------------------------
    const [clientInput, setClientInput] = useState("");
    const [clientSelected, setClientSelected] = useState<FireClientOption | null>(null);
    const [clientLoading, setClientLoading] = useState(false);
    const [clientOptions, setClientOptions] = useState<FireClientOption[]>([]);

    // Search remoto
    useEffect(() => {
        if (!showClientSelect) return;
        if (!clientInput) {
            setClientOptions([]);
            return;
        }

        setClientLoading(true);

        debounce(() => {
            clientService
                .getSelect(1, clientInput.trim())
                .then((result: any) => {
                    if (result instanceof Error) return;

                    const options = result?.data?.map((c: any) => ({
                        id: c.id,
                        label: c.name,
                    }));

                    setClientOptions(options ?? []);
                })
                .finally(() => setClientLoading(false));
        });

    }, [clientInput]);

    // Quando seleciona cliente
    const handleSelectClient = (value: FireClientOption | null) => {
        setClientSelected(value);
        onChangeClient?.(value?.id ?? null);
    };

    // ------------------------
    // Lógica de período (igual v1)
    // ------------------------
    const getDateRangeFromPeriod = (period: string): [string, string] => {
        const now = new Date();
        let start = now;
        let end = now;

        switch (period) {
            case "15_dias":
                start = subDays(now, 15);
                break;
            case "1_mes":
                start = startOfMonth(now);
                end = endOfMonth(now);
                break;
            case "2_meses":
                start = startOfMonth(subMonths(now, 1));
                end = endOfMonth(now);
                break;
            case "3_meses":
                start = startOfMonth(subMonths(now, 2));
                end = endOfMonth(now);
                break;
            case "1_ano":
                start = startOfMonth(subYears(now, 1));
                end = endOfMonth(now);
                break;
            case "mes_anterior":
                start = startOfMonth(subMonths(now, 1));
                end = endOfMonth(subMonths(now, 1));
                break;
            case "proximo_mes":
                start = startOfMonth(addMonths(now, 1));
                end = endOfMonth(addMonths(now, 1));
                break;
        }

        return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
    };

    const handlePeriodChange = (e: SelectChangeEvent) => {
        const value = e.target.value;
        setPeriodType(value);

        if (value !== "personalizado") {
            const [d1, d2] = getDateRangeFromPeriod(value);
            setStartDate(d1);
            setEndDate(d2);
            onChangeDateStart?.(d1);
            onChangeDateEnd?.(d2);
        }
    };

    const handleSearchClick = () => {
        onClickSearch?.();
        onClickRefresh?.();
    };

    const effectiveShowSearchButton =
        typeof showSearchButton === "boolean" ? showSearchButton : showRefreshButton;

    const effectiveShowNewButton =
        typeof showNewQuoteButton === "boolean" ? showNewQuoteButton : showNewFireButton;

    const handleNewClick = () => {
        onClickNewQuote?.();
        onClickNewFire?.();
    };

    return (
        <Box component={Paper} padding={2} mx={1} display="flex" flexDirection="column" gap={2}>
            {/* ---------------------- */}
            {/* LINHA SUPERIOR – FILTROS */}
            {/* ---------------------- */}
            <Grid container spacing={2} alignItems="center">

                {/* AUTOCOMPLETE CLIENTE */}
                {showClientSelect && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            loading={clientLoading}
                            options={clientOptions}
                            value={clientSelected}
                            onChange={(_, v) => handleSelectClient(v)}
                            onInputChange={(_, value) => setClientInput(value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Imobiliária" />
                            )}
                        />
                    </Grid>
                )}

                {/* PERÍODO */}
                {showDateRange && (
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Período</InputLabel>
                            <Select
                                value={periodType}
                                label="Período"
                                onChange={handlePeriodChange}
                            >
                                <MenuItem value="15_dias">Últimos 15 dias</MenuItem>
                                <MenuItem value="mes_anterior">Mês Anterior</MenuItem>
                                <MenuItem value="1_mes">Mês Atual</MenuItem>
                                <MenuItem value="proximo_mes">Próximo Mês</MenuItem>
                                <MenuItem value="2_meses">Últimos 2 meses</MenuItem>
                                <MenuItem value="3_meses">Últimos 3 meses</MenuItem>
                                <MenuItem value="1_ano">Último ano</MenuItem>
                                <MenuItem value="personalizado">Personalizado</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {/* DATAS PERSONALIZADAS */}
                {showDateRange && periodType === "personalizado" && (
                    <>
                        <Grid item xs={12} sm={4} md={2}>
                            <TextField
                                fullWidth
                                type="date"
                                size="small"
                                label="Data Inicial"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    onChangeDateStart?.(e.target.value);
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4} md={2}>
                            <TextField
                                fullWidth
                                type="date"
                                size="small"
                                label="Data Final"
                                value={endDate}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    onChangeDateEnd?.(e.target.value);
                                }}
                            />
                        </Grid>
                    </>
                )}

                {/* STATUS */}
                {showStatusSelect && (
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    onChangeStatus?.(e.target.value);
                                }}
                            >
                                <MenuItem value="todos">Todos</MenuItem>
                                <MenuItem value="pending">Pendente</MenuItem>
                                <MenuItem value="open">Aguardando contratação</MenuItem>
                                <MenuItem value="expired">Expirado</MenuItem>
                                <MenuItem value="canceled">Cancelado</MenuItem>
                                <MenuItem value="sending">Enviando</MenuItem>
                                <MenuItem value="hired">Contratado</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                )}
            </Grid>

            {/* ---------------------- */}
            {/* LINHA DE BUSCA + BOTÕES */}
            {/* ---------------------- */}
            <Grid container spacing={2} alignItems="center">

                {/* CAMPO DE PESQUISA */}
                <Grid item xs={12} sm={8} md={4}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Pesquisar por Nome, Documento..."
                        value={textSearch}
                        onChange={(e) => onChangeTextSearch?.(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            onClearTextSearch?.();
                                            onChangeTextSearch?.("");
                                        }}
                                    >
                                        <Clear />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* BOTÃO PESQUISAR */}
                {effectiveShowSearchButton && (
                    <Grid item xs={12} sm={4} md="auto">
                        <Button
                            variant="contained"
                            startIcon={<Search />}
                            onClick={handleSearchClick}
                            fullWidth={isSmall}
                        >
                            Pesquisar
                        </Button>
                    </Grid>
                )}

                {/* BOTÃO NOVO ORÇAMENTO */}
                {effectiveShowNewButton && (
                    <Grid item xs={12} sm="auto" md sx={{ textAlign: "right" }}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddOutlined />}
                            onClick={handleNewClick}
                            fullWidth={isSmall}
                        >
                            Novo Orçamento
                        </Button>
                    </Grid>
                )}
            </Grid>

            {/* RESUMO DO PERÍODO */}
            {showDateRange && (
                <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", color: "text.secondary", ml: 1 }}
                >
                    Período: {format(parseISO(startDate), "dd/MM/yyyy")} -{" "}
                    {format(parseISO(endDate), "dd/MM/yyyy")}
                </Typography>
            )}
        </Box>
    );
}
